import { studyQueryKeys } from '@/config/queryKeys';
import { TABLE_PAGE_SIZE_OPTIONS } from '@/constants';
import { useDateFormatter } from '@/hooks';
import { createAgGridDatasource } from '@/lib/createAgGridDatasource';
import type { PaginatedStudiesResponse, StudyPublicGetMany } from '@/types';
import { flushColumnState, sizeColumnsOnFirstVisit } from '@/utils/flushColumnState';
import { Table } from '@nautilusbio/nb-design-system/Table';
import type { ColDef } from 'ag-grid-enterprise';
import type { AgGridReact } from 'ag-grid-react';
import clsx from 'clsx';
import React, { type FC, type RefObject, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import tableStyles from '../../../styles/components/table.module.css';
import globalStyles from '../../../styles/index.module.css';
import styles from '../styles.module.css';

import AvatarCellRenderer from './AvatarCellRenderer';
import StudyContactCell from './StudyContactCell';
import StudyNameCell from './StudyNameCell';

export type StudiesTableProps = {
  gridRef: RefObject<AgGridReact<StudyRow> | null>;
  onFilteredTotalChanged?: (total: number) => void;
  selectedOrg: string;
};

// StudyRow extends the API type with optional UI-only fields we display in the table
export type StudyRow = StudyPublicGetMany &
  Partial<{
    contact: { name?: string | null; email?: string | null } | null;
    org_name: string;
    num_samples: number;
    num_samples_passed_qc: number;
    num_instrument_runs: number;
    num_proteins: number;
    num_proteoform_groups: number;
  }>;

const STUDIES_PAGE_SIZE_KEY = 'studies-table-pageSize';
const DEFAULT_PAGE_SIZE = 25;

const TableData: FC<StudiesTableProps> = ({ gridRef, onFilteredTotalChanged, selectedOrg }) => {
  const { t } = useTranslation('studies');
  const formatDate = useDateFormatter();
  const lastSavedPageSizeRef = useRef<number>(DEFAULT_PAGE_SIZE);
  const isInitialLoadRef = useRef<boolean>(true);
  const lastFallbackSortColIdRef = useRef<string | undefined>(undefined);

  // Column Definitions: Defines & controls grid columns.
  // Using useMemo to react to formatDate changes (user date format preference)
  const colDefs = useMemo<ColDef<StudyRow>[]>(
    () => [
      // id — hidden, used internally
      { field: 'id', headerName: 'ID', hide: true, sortable: true, filter: 'agTextColumnFilter' },

      // Study name — link to detail
      {
        cellClass: styles.tableCell,
        cellRenderer: StudyNameCell,
        field: 'name',
        headerName: t('columnHeaders.study'),
        maxWidth: 400,
        width: 200,
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: [
            'contains',
            'notContains',
            'equals',
            'notEqual',
            'startsWith',
            'endsWith',
            'blank',
            'notBlank',
          ],
          maxNumConditions: 2,
          buttons: ['reset', 'apply'],
          defaultOption: 'contains',
          debounceMs: 500,
        },
      },

      // Contact
      {
        cellClass: styles.tableCell,
        field: 'contact',
        headerName: t('columnHeaders.contact'),
        filter: false,
        maxWidth: 250,
        cellRenderer: (params: { data: StudyRow; value: StudyRow['contact'] }) => (
          <StudyContactCell id={params.data.id} value={params.value} />
        ),
      },

      // Description
      {
        cellClass: styles.tableCell,
        field: 'description',
        flex: 1,
        headerName: t('columnHeaders.description'),
        filter: false,
        sortable: true,
      },

      // Samples count
      {
        field: 'num_samples',
        headerName: t('columnHeaders.numSamples'),
        headerTooltip: t('columnHeaders.numSamples'),
        maxWidth: 120,
        sortable: true,
        filter: 'agNumberColumnFilter',
        valueFormatter: ({ value }) => (typeof value === 'number' ? String(value) : '—'),
      },

      // Organization
      {
        field: 'org_name',
        headerName: t('columnHeaders.organization'),
        headerTooltip: t('columnHeaders.organization'),
        maxWidth: 120,
        sortable: true,
        filter: 'agTextColumnFilter',
        valueFormatter: ({ value }) => (value && typeof value === 'string' ? value : '—'),
      },

      // Created date
      {
        cellClass: styles.tableCell,
        field: 'created_at',
        headerName: t('columnHeaders.created'),
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: ['equals', 'notEqual', 'lessThan', 'greaterThan', 'inRange', 'blank', 'notBlank'],
          maxNumConditions: 2,
          buttons: ['reset', 'apply'],
        },
        maxWidth: 210,
        sortable: true,
        valueFormatter: ({ value }) => formatDate(value),
      },

      // Updated date
      {
        cellClass: styles.tableCell,
        field: 'updated_at',
        headerName: t('columnHeaders.updated'),
        filter: 'agDateColumnFilter',
        sortable: true,
        maxWidth: 210,
        valueFormatter: ({ value }) => formatDate(value),
      },

      // Created By
      {
        cellClass: tableStyles.tableCellCenter,
        cellRenderer: AvatarCellRenderer,
        field: 'created_by_full_name',
        headerName: t('columnHeaders.createdBy'),
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains', 'equals', 'startsWith', 'blank', 'notBlank'],
          maxNumConditions: 2,
          buttons: ['reset', 'apply'],
          defaultOption: 'contains',
          debounceMs: 500,
        },
      },

      // Updated By
      {
        cellClass: tableStyles.tableCellCenter,
        cellRenderer: AvatarCellRenderer,
        field: 'updated_by_full_name',
        headerName: t('columnHeaders.updatedBy'),
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          filterOptions: ['contains', 'equals', 'startsWith', 'blank', 'notBlank'],
          maxNumConditions: 2,
          buttons: ['reset', 'apply'],
          defaultOption: 'contains',
          debounceMs: 500,
        },
      },

      // — Hidden stat columns —
      {
        field: 'num_samples_passed_qc',
        headerName: 'QC-Passed Samples',
        hide: true,
        maxWidth: 130,
        sortable: true,
        filter: 'agNumberColumnFilter',
        valueFormatter: ({ value }) => (typeof value === 'number' ? String(value) : '—'),
      },
      {
        field: 'num_instrument_runs',
        headerName: 'Instrument Runs',
        hide: true,
        maxWidth: 130,
        sortable: true,
        filter: 'agNumberColumnFilter',
        valueFormatter: ({ value }) => (typeof value === 'number' ? String(value) : '—'),
      },
      {
        field: 'num_proteins',
        headerName: 'Proteins',
        hide: true,
        maxWidth: 120,
        sortable: true,
        filter: 'agNumberColumnFilter',
        valueFormatter: ({ value }) => (typeof value === 'number' ? String(value) : '—'),
      },
      {
        field: 'num_proteoform_groups',
        headerName: 'Proteoform Groups',
        hide: true,
        maxWidth: 150,
        sortable: true,
        filter: 'agNumberColumnFilter',
        valueFormatter: ({ value }) => (typeof value === 'number' ? String(value) : '—'),
      },
    ],
    [formatDate, t],
  );

  const tableContainerClasses = useMemo(() => ({ className: clsx(styles.table, globalStyles.scrollbars) }), []);

  // AG Grid server-side datasource using shared factory, backed by TanStack Query
  const datasource = useMemo(
    () =>
      createAgGridDatasource<StudyRow, PaginatedStudiesResponse>(
        studyQueryKeys.endpoints.listSearch(selectedOrg),
        undefined,
        {
          onTotalChanged: onFilteredTotalChanged,
          onSortFallback: async colId => {
            // Don't toast when a persisted sort is restored from localStorage
            // on mount and immediately rejected by the backend — only
            // genuine user-initiated sort clicks should surface feedback.
            if (isInitialLoadRef.current) return;
            // Dedupe repeat getRows requests for the same sort attempt; a
            // fresh sort action resets this via onSortChanged below.
            if (colId === lastFallbackSortColIdRef.current) return;
            lastFallbackSortColIdRef.current = colId;
            const { toast } = await import('@nautilusbio/nb-design-system/Toaster');
            toast.info("Sorting by this column isn't supported yet — showing the default sort instead.");
          },
        },
      ),
    [selectedOrg, onFilteredTotalChanged],
  );

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption('serverSideDatasource', datasource);
    }
  }, [datasource, gridRef]);

  return (
    <Table<StudyRow>
      columnDefs={colDefs}
      container={tableContainerClasses}
      domLayout="normal"
      onGridReady={e => {
        e.api.setGridOption('serverSideDatasource', datasource);
        sizeColumnsOnFirstVisit(e.api, 'studies-table');
        const saved = localStorage.getItem(STUDIES_PAGE_SIZE_KEY);
        const parsed = saved !== null ? Number.parseInt(saved, 10) : Number.NaN;
        const initial =
          !Number.isNaN(parsed) && (TABLE_PAGE_SIZE_OPTIONS as readonly number[]).includes(parsed)
            ? parsed
            : DEFAULT_PAGE_SIZE;
        lastSavedPageSizeRef.current = initial;
        e.api.updateGridOptions({ paginationPageSize: initial });
      }}
      onPaginationChanged={e => {
        const newSize = e.api.paginationGetPageSize();
        if (newSize !== lastSavedPageSizeRef.current) {
          localStorage.setItem(STUDIES_PAGE_SIZE_KEY, String(newSize));
          lastSavedPageSizeRef.current = newSize;
        }
      }}
      onGridPreDestroyed={e => flushColumnState(e.api, 'studies-table')}
      onFirstDataRendered={() => {
        isInitialLoadRef.current = false;
      }}
      onSortChanged={() => {
        lastFallbackSortColIdRef.current = undefined;
      }}
      pagination
      paginationPageSize={DEFAULT_PAGE_SIZE}
      paginationPageSizeSelector={TABLE_PAGE_SIZE_OPTIONS}
      tableId="studies-table"
      enableResponsiveResize={false}
      resizeDebounceMs={150}
      ref={gridRef}
      rowModelType="serverSide"
      zebraStriping
    />
  );
};

export default TableData;
